import React, { ReactNode, useEffect } from 'react';
import clsx from 'clsx';
import { ModalsKeys } from 'global';
import { Grid } from '@mui/material';
import { DndProvider } from 'react-dnd';
import { useQuery } from '@apollo/client';
import { useParams } from 'react-router-dom';
import makeStyles from '@mui/styles/makeStyles';
import { useDispatch, useSelector } from 'react-redux';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { FormProvider, useForm } from 'react-hook-form';
import { PageContainer } from 'components/shared/Page/PageContainer';
import { ProductCatalogDialog, ProposalProductsDialog } from 'modules/desking';
import { ProposalProductsResponse, ProposalsDataResponse } from 'modules/aftermarketMenuConstructor/api/types';
import { GET_PROPOSAL_MENUS_WITH_OPTIONS, GET_PROPOSAL_PRODUCTS } from 'modules/aftermarketMenuConstructor/api';
import { AddEditMenuTemplateUrlParams, MenuTemplateGeneralFormInputs } from 'modules/aftermarketMenuConstructor/types';
import { ConstructorPricingInfoDialog } from 'modules/desking/ui/dialogs/ProposalProducts/ConstructorPricingInfoDialog';
import { AddEditMenuOptionDialog, SaveAsConfirmationDialog, ShareMenuConfirmationDialog } from '../../dialogs';
import { useAftermarketPermissions } from 'modules/aftermarketMenuConstructor/hooks/useAftermarketPermissions';
import { menuConstructorActions, menuConstructorSelectors } from '../../../model';
import { GeneralInformationCard, HeaderCard, ProductsCard, MenuOptionsCard } from '../../components';

const modalsWithKeys: Partial<Record<ModalsKeys, ReactNode>> = {
  [ModalsKeys.AftermarketAddEditMenuOptionDialog]: <AddEditMenuOptionDialog />,
  [ModalsKeys.ProposalProductsDialog]: <ProposalProductsDialog />,
  [ModalsKeys.ProductCatalogDialog]: <ProductCatalogDialog />,
  [ModalsKeys.ConstructorPricingInfoDialog]: <ConstructorPricingInfoDialog />,
  [ModalsKeys.AftermarketSaveAsDialog]: <SaveAsConfirmationDialog />,
  [ModalsKeys.AftermarketShareMenuConfirmationDialog]: <ShareMenuConfirmationDialog />,
};

export const AddEditMenuTemplatePage = () => {
  const classes = useStyles();

  const dispatch = useDispatch();

  const isLoading = useSelector(menuConstructorSelectors.isLoading);
  const isMenuSectionCollapsed = useSelector((state: any) =>
    menuConstructorSelectors.isSectionCollapsed(state, 'menu')
  );

  const { hasWriteAccess, hasReadOnlyAccess } = useAftermarketPermissions();

  const { proposalMenuId } = useParams<AddEditMenuTemplateUrlParams>();

  const form = useForm<MenuTemplateGeneralFormInputs>({
    defaultValues: {
      name: '',
      description: '',
      partnerLinksToShareMenu: [],
      linkedToCriterias: [],
    },
  });

  const { loading: proposalMenuLoading, error } = useQuery<ProposalsDataResponse>(GET_PROPOSAL_MENUS_WITH_OPTIONS, {
    skip: !proposalMenuId,
    variables: { input: { proposalMenuId } },
    fetchPolicy: 'no-cache',
    onCompleted(response) {
      if (!Array.isArray(response?.proposalMenus)) return;
      const proposalMenu = response.proposalMenus[0];
      dispatch(menuConstructorActions.setMenu(proposalMenu));
      dispatch(menuConstructorActions.setInitialMenu(proposalMenu));
      form.reset({
        name: proposalMenu.name,
        description: proposalMenu.description,
        partnerLinksToShareMenu:
          proposalMenu.linkedToCriterias?.map(linkedToCriteria => ({
            value: linkedToCriteria.partnerLinkId,
            label: linkedToCriteria.account.name!,
          })) ?? [],
        linkedToCriterias:
          proposalMenu.linkedToCriterias?.map(linkedToCriteria => ({
            partnerLinkId: linkedToCriteria.partnerLinkId,
            isReadOnly: linkedToCriteria.isReadOnly,
          })) ?? [],
      });
    },
  });

  const { loading: isProductsLoading } = useQuery<ProposalProductsResponse>(GET_PROPOSAL_PRODUCTS, {
    variables: {
      input: {
        sourceOfCreation: ['base'],
      },
    },
    onCompleted(response) {
      if (!Array.isArray(response?.proposalProducts)) return;
      dispatch(menuConstructorActions.setProducts(response.proposalProducts));
    },
  });

  useEffect(() => {
    dispatch(menuConstructorActions.setIsLoading(proposalMenuLoading));
  }, [proposalMenuLoading]);

  useEffect(
    () => () => {
      dispatch(menuConstructorActions.clearState());
    },
    [dispatch]
  );

  return (
    <PageContainer modals={modalsWithKeys} error={error}>
      {hasWriteAccess || hasReadOnlyAccess ? (
        <DndProvider backend={HTML5Backend}>
          <FormProvider {...form}>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <HeaderCard />
              </Grid>

              <Grid item xs={12} className={clsx({ [classes.skeleton]: isLoading })}>
                <GeneralInformationCard />
              </Grid>

              {isMenuSectionCollapsed && (
                <Grid item xs={6} className={clsx({ [classes.skeleton]: isLoading })}>
                  <ProductsCard isProductsLoading={isProductsLoading} />
                </Grid>
              )}

              <Grid item xs={!isMenuSectionCollapsed ? 12 : 6} className={clsx({ [classes.skeleton]: isLoading })}>
                <MenuOptionsCard isProductsLoading={isProductsLoading} />
              </Grid>
            </Grid>
          </FormProvider>
        </DndProvider>
      ) : (
        <h3>Access Denied</h3>
      )}
    </PageContainer>
  );
};

const useStyles = makeStyles({
  skeleton: {
    height: 150,
  },
});
